import { Button, Icon, Pagination, Radio } from "@ab-inbev-maz/compass"
import FileUploader from "../commos/FileUploader"
import z from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { createCraftSchema } from "@/schemas/craft/create"
import { useCraftStore } from "@/stores/craftStore"
import { useCraftContext } from "@/contexts/craftContext"
import { useEffect, useMemo, useState } from "react"
import { api } from "@/services/api"
import { useAuthStore } from "@/features/sso-auth/store/authStore"
import { sanitizeRichTextHtml } from "@/utils/sanitizeRichTextHtml"

const firstStepSchema = createCraftSchema.pick({ elementType: true });

export const FirstStep = () => {
    const {
        register,
        handleSubmit,
        formState: {
            errors,
            isSubmitting
        } } = useForm<z.infer<typeof firstStepSchema>>({
            resolver: zodResolver(firstStepSchema),
            defaultValues: {
                elementType: "",
            },
        });

    const { onFirstStepComplete } = useCraftContext();
    const craftData = useCraftStore(state => state.data);

    return (
        <form onSubmit={handleSubmit(onFirstStepComplete)} className={`grid px-8 pt-4 pb-6 gap-4 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
            <p className='text-sm text-gray-500'>Primero, selecciona que elemento vas a trabajar:</p>

            <div className="grid gap-y-4">
                {craftData?.actions?.map((element) => (
                    <div key={element.id} className="flex items-center gap-4">
                        <Radio
                            value={element.id}
                            {...register("elementType")}
                        />
                        <div className="flex flex-col gap-0.5 min">
                            <span className="text-l font-semibold">{element.name}</span>
                            <span className="text-sm text-gray-500">{element.description}</span>
                        </div>
                    </div>
                ))}
                {errors.elementType && <p className="text-xs text-red-500">{errors.elementType.message}</p>}
            </div>

            <div className="flex justify-start mt-4">
                <Button type="submit" className="min-w-[200px]">Continuar</Button>
            </div>
        </form>
    )
}

const secondStepSchema = createCraftSchema.pick({ formatType: true });

export const SecondStep = () => {
    const {
        register,
        handleSubmit,
        formState: {
            errors,
            isSubmitting
        } } = useForm<z.infer<typeof secondStepSchema>>({
            resolver: zodResolver(secondStepSchema),
            defaultValues: {
                formatType: "",
            },
        });

    const { onSecondStepComplete } = useCraftContext();
    const craftData = useCraftStore(state => state.data);

    return (
        <form onSubmit={handleSubmit(onSecondStepComplete)} className={`grid px-8 pt-4 pb-6 gap-4 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
            <p className="text-sm text-gray-500">Ahora, selecciona el tipo de formato que vas a trabajar:</p>

            <div className="grid gap-y-4">
                {craftData?.formats?.map((format) => (
                    <div key={format.id} className="flex items-center gap-4">
                        <Radio
                            value={format.id}
                            {...register("formatType")}
                        />
                        <img src={`/images/${format.id}_upd.png`} alt="" className="w-[175px] h-auto object-contain" />
                        <div className="flex flex-col gap-0.5 min">
                            <span className="text-l font-semibold">{format.name}</span>
                            <span className="text-sm text-gray-500">{format.description}</span>
                        </div>
                    </div>
                ))}
                {errors.formatType && <p className="text-xs text-red-500">{errors.formatType.message}</p>}
            </div>

            <div className="flex justify-start mt-4">
                <Button type="submit" className="min-w-[200px]">Continuar</Button>
            </div>
        </form>
    )
}

const layoutStepSchema = createCraftSchema.pick({ taxonomy: true });

const LAYOUT_OPTIONS = [
    {
        id: 'default',
        name: 'Vertical',
        description: 'Los SKU se apilarán de forma vertical, uno encima del otro, manteniendo la alineación central.',
        image: '/images/layout_vertical.png',
    },
    {
        id: 'default_top',
        name: 'Vertical - Espacio Superior',
        description: 'Los SKU se apilarán de forma vertical, uno encima del otro, manteniendo la alineación central.',
        image: '/images/layout_vertical_top.png',
    },
    {
        id: 'lineal',
        name: 'Lineal',
        description: 'Los SKU se alinearán dinámicamente uno junto a otro, manteniendo un espacio uniforme entre cada uno.',
        image: '/images/layout_linear.png',
    },
    {
        id: 'overlap',
        name: 'Sobrepuesto',
        description: 'Los SKU se superpondrán parcialmente entre sí, creando un efecto de profundidad y dinamismo.',
        image: '/images/layout_overlap.png',
    },
    {
        id: 'grid',
        name: 'Cuadrícula',
        description: 'Los SKU se distribuirán en una cuadrícula ordenada, ocupando el espacio de manera uniforme.',
        image: '/images/layout_grid.png',
    },
];

const getLayoutOptions = (defaultTaxonomy: string) => {
    const defaultIndex = LAYOUT_OPTIONS.findIndex((layout) => layout.id === defaultTaxonomy);
    if (defaultIndex <= 0) return LAYOUT_OPTIONS;

    const preferredLayout = LAYOUT_OPTIONS[defaultIndex];
    return [preferredLayout, ...LAYOUT_OPTIONS.filter((layout) => layout.id !== defaultTaxonomy)];
};

export const LayoutStep = () => {
    const defaultTaxonomyFromStore = useAuthStore((state) => state.default_taxonomy ?? state.user?.default_taxonomy ?? "default");
    const defaultTaxonomy = LAYOUT_OPTIONS.some((layout) => layout.id === defaultTaxonomyFromStore) ? defaultTaxonomyFromStore : "default";
    const layoutOptions = useMemo(() => getLayoutOptions(defaultTaxonomy), [defaultTaxonomy]);

    const {
        register,
        handleSubmit,
        formState: {
            errors,
            isSubmitting
        } } = useForm<z.infer<typeof layoutStepSchema>>({
            resolver: zodResolver(layoutStepSchema),
            defaultValues: {
                taxonomy: defaultTaxonomy,
            },
        });

    const { onLayoutStepComplete } = useCraftContext();

    return (
        <form onSubmit={handleSubmit(onLayoutStepComplete)} className={`grid px-8 pt-4 pb-6 gap-4 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
            <p className="text-sm text-gray-500">Selecciona el tipo de taxonomía para organizar los SKU:</p>

            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                {layoutOptions.map((layout) => (
                    <label key={layout.id} className="flex items-start gap-4 p-4 border rounded-lg hover:border-gray-400 transition-colors cursor-pointer">
                        <Radio
                            value={layout.id}
                            {...register("taxonomy")}
                            defaultChecked={layout.id === defaultTaxonomy}
                        />
                        <img
                            src={layout.image}
                            alt={layout.name}
                            className="w-[180px] h-[180px] object-contain rounded-sm border flex-shrink-0"
                        />
                        <div className="flex flex-col gap-1 flex-1">
                            <span className="text-l font-semibold">{layout.name}</span>
                            <span className="text-sm text-gray-500">{layout.description}</span>
                        </div>
                    </label>
                ))}
            </div>
            {errors.taxonomy && <p className="text-xs text-red-500">{errors.taxonomy.message}</p>}

            <div className="flex justify-start mt-4">
                <Button type="submit" className="min-w-[200px]">Continuar</Button>
            </div>
        </form>
    )
}

const thirdStepSchema = createCraftSchema.pick({ image_rule_id: true });

const FORMAT_PLACEHOLDER_IMAGES: Record<string, string> = {
    '16_9': '/images/16_9_upd.png',
    '1_1': '/images/1_1_upd.png',
    '9_16': '/images/9_16_upd.png',
};

export const ThirdStep = () => {
    const {
        register,
        handleSubmit,
        formState: {
            errors,
        } } = useForm<z.infer<typeof thirdStepSchema>>({
            resolver: zodResolver(thirdStepSchema),
            defaultValues: {
                image_rule_id: "",
            },
        });

    const { onThirdStepComplete } = useCraftContext();
    const taxonomies = useCraftStore(state => state.data?.taxonomies);
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
    const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const loadImages = async () => {
            if (!taxonomies) return;

            const initialLoading: Record<string, boolean> = {};
            for (const taxonomy of taxonomies) {
                if (taxonomy.preview_image_url) {
                    initialLoading[taxonomy.id] = true;
                }
            }
            setLoadingImages(initialLoading);

            const urls: Record<string, string> = {};

            await Promise.all(
                taxonomies.map(async (taxonomy) => {
                    if (!taxonomy.preview_image_url) return;
                    try {
                        const url = taxonomy.preview_image_url.replace(/^\/api\/v1/, '');
                        const response = await api.get(url, {
                            responseType: 'blob',
                        });
                        const blobUrl = URL.createObjectURL(response.data);
                        urls[taxonomy.id] = blobUrl;
                    } catch (error) {
                        console.error(`Error loading image for taxonomy ${taxonomy.id}:`, error);
                    } finally {
                        setLoadingImages(prev => ({ ...prev, [taxonomy.id]: false }));
                    }
                })
            );

            setImageUrls(urls);
        };

        void loadImages();

        return () => {
            Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
        };
    }, [taxonomies]);

    const getPlaceholderImage = (formatType: string) => {
        return FORMAT_PLACEHOLDER_IMAGES[formatType] || '/images/1_1_upd.png';
    };

    return (
        <form onSubmit={handleSubmit(onThirdStepComplete)} className='grid px-8 pt-4 pb-6 gap-4'>
            <p className="text-sm text-gray-500">Ahora define una taxonomía para organizar los contenidos dentro del formato seleccionado:</p>

            <div className="flex flex-col gap-4">
                <div className="grid gap-y-4 grid-cols-1 lg:grid-cols-2">
                    {taxonomies?.map((taxonomy) => (
                        <div key={taxonomy.id} className="flex flex-col lg:flex-row items-start gap-4 p-4 border rounded-lg hover:border-gray-400 transition-colors">
                            <Radio value={taxonomy.id} {...register("image_rule_id")} />
                            <div className="relative w-[185px] max-h-[280px] flex-shrink-0">
                                {loadingImages[taxonomy.id] && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-sm z-10">
                                        <div className="w-8 h-8 border-3 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                                    </div>
                                )}
                                <img
                                    src={imageUrls[taxonomy.id] || getPlaceholderImage(taxonomy.format_type)}
                                    alt={taxonomy.name}
                                    className="w-[185px] max-h-[280px] object-contain rounded-sm border"
                                    onError={(e) => {
                                        e.currentTarget.src = getPlaceholderImage(taxonomy.format_type);
                                    }}
                                />
                            </div>
                            <div className="flex flex-col gap-2 flex-1">
                                <span className="text-l font-semibold">{taxonomy.name}</span>
                                <ul className="flex flex-col gap-1">
                                    {Object.entries(taxonomy.specifications).map(([key, spec]) => spec && (
                                        <li key={key} className="flex items-center gap-2 text-sm text-gray-700">
                                            {spec.color && (
                                                <span
                                                    className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                                                    style={{ backgroundColor: spec.color }}
                                                    title={spec.color}
                                                />
                                            )}
                                            <span className={!spec.color ? 'pl-6' : ''}>{spec.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
                {errors.image_rule_id && <p className="text-xs text-red-500">{errors.image_rule_id.message}</p>}
            </div>

            <div className="flex justify-start mt-4">
                <Button type="submit" className="min-w-[200px]">Continuar</Button>
            </div>
        </form>
    )
}

const fourthStepSchema = createCraftSchema.pick({ template_id: true });

export const FourthStep = () => {
    const {
        register,
        handleSubmit,
        watch,
        formState: {
            errors,
        } } = useForm<z.infer<typeof fourthStepSchema>>({
            resolver: zodResolver(fourthStepSchema),
            defaultValues: {
                template_id: "",
            },
        });

    const { onFourthStepComplete } = useCraftContext();

    const templates = useCraftStore(state => state.data?.templates);
    const pagination = useCraftStore(state => state.pagination);
    const getTemplates = useCraftStore(state => state.getTemplates);

    const template_id = watch("template_id");

    const handlePageChange = (page: number) => {
        void getTemplates({ page });
    }

    return (
        <div className='grid px-8 pt-4 pb-6 gap-4'>
            <p className="text-sm text-gray-500">Ahora selecciona uno de los fondos para tu comunicación:</p>

            <div className="flex flex-col gap-4">
                <form id="template-form" onSubmit={handleSubmit(onFourthStepComplete)} className="grid gap-2">
                    <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(130px,1fr))]">
                        {templates?.map((template) => (
                            <div key={template.id} className="relative mb-5">
                                <label>
                                    <input type="radio" id={`template-${template.id}`} value={template.id} {...register("template_id")} className="hidden" />
                                    {
                                        template_id === `${template.id}` && (
                                            <div className="text-white bg-green-500 rounded-full absolute top-0 right-0 p-1 translate-x-1/3 -translate-y-1/3">
                                                <div className="w-4 h-4">
                                                    <Icon name="validation/check" />
                                                </div>
                                            </div>)
                                    }
                                    <img
                                        key={template.id}
                                        src={template.image}
                                        alt=""
                                        className={`w-full object-contain rounded-lg border-2 overflow-hidden cursor-pointer ${template_id === String(template.id) ? 'border-green-500' : 'border-gray-300'}`}
                                    />

                                    <p className="mt-2 text-center text-xs text-gray-500">{template.name}</p>
                                </label>
                            </div>
                        ))}
                    </div>
                    {errors.template_id && <p className="text-xs text-red-500">{errors.template_id.message}</p>}
                </form>
                <Pagination
                    key={pagination.formatType}
                    className="flex-row! !flex-nowrap !p-0"
                    page={pagination.current}
                    total={pagination.total}
                    size={pagination.size}
                    onChangePage={handlePageChange}
                />
            </div>

            <div className="flex justify-start">
                <Button form="template-form" type="submit" className="min-w-[200px]">Continuar</Button>
            </div>
        </div>
    )
}


const tycStepSchema = createCraftSchema.pick({ legal: true });

export const TycStep = () => {
    const { onTycStepComplete } = useCraftContext();
    const legals = useCraftStore(state => state.data?.legals);

    const {
        register,
        handleSubmit,
        formState: {
            errors,
            isSubmitting
        } } = useForm<z.infer<typeof tycStepSchema>>({
            resolver: zodResolver(tycStepSchema),
            defaultValues: {
                legal: "",
            },
        });

    return (
        <form onSubmit={handleSubmit(onTycStepComplete)} className="px-5 pb-6">
            <p className="text-sm text-gray-500">Selecciona el texto legal que deseas asociar:</p>
            <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex flex-col gap-4 w-1/2">
                    {legals?.map((legalItem) => (
                        <label key={legalItem.id} className="flex items-start gap-4 p-4 border rounded-lg hover:border-gray-400 transition-colors cursor-pointer mb-5">
                            <Radio
                                value={String(legalItem.id)}
                                {...register("legal")}
                            />
                            <div className="flex flex-col gap-1 flex-1">
                                <p className="text-sm">
                                    <span className="font-semibold">Nombre: </span>
                                    {legalItem.name}
                                </p>
                                <div className="text-sm">
                                    <span className="font-semibold">Texto: </span>
                                    <div
                                        className="mt-1 whitespace-pre-line [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(legalItem.legal_text) }}
                                    />
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="font-semibold">Color:</span>
                                    <span
                                        className="inline-block w-[80px] rounded border border-gray-300"
                                        style={{ backgroundColor: legalItem.color, height: '1.25em' }}
                                    />
                                    <span className="text-gray-500">{legalItem.color}</span>
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
                {errors.legal && <p className="text-xs text-red-500 mt-2">{errors.legal.message}</p>}
                <div className="flex justify-start mt-4">
                    <Button type="submit" className="min-w-[200px]">Continuar</Button>
                </div>
            </div>
        </form>
    )
}

export const FifthStep = () => {
    const { onFifthStepComplete, isFetching } = useCraftContext();

    return (
        <div className='grid px-8 pt-4 pb-6 gap-4'>
            <p className="text-sm text-gray-500">Ahora carga el archivo para enlazar la información:</p>

            <div className={`${isFetching ? 'opacity-50 pointer-events-none' : ''}`}>
                <FileUploader
                    title="Carga tu archivo aquí"
                    buttonText=""
                    onFileSelect={onFifthStepComplete}
                    acceptedFileTypes=".csv, .xlsx, .xls"
                    maxSizeMB={10}
                />
            </div>
        </div>
    )
}
